import { ICheckinType } from '../../../journal/model/ICheckin';
import { ICareMapSection } from '../../../caremap/ICareMap';
import { CustomOrgTemplates } from './CustomOrgTemplates';

export interface INoteTemplate {
    templateId: string;
    label: string;
    body: string;
    target: INoteTemplateTarget;
    focusLocation?: number;
    useLabelForTitle?: boolean;
    isButton?: boolean;
};

export interface ICareMapNoteTemplateTarget {
    section: ICareMapSection;
    forMember?: boolean;
};

export interface ICheckinNoteTemplateTarget {
    section: 'checkin';
    checkinType?: ICheckinType;
};

export type INoteTemplateTarget = ICareMapNoteTemplateTarget | ICheckinNoteTemplateTarget;

type ITemplateId =
    // community feed
    'ride' |
    'groceries' |
    'rest' |
    'errand' |
    'pet-care' |
    'visits' |

    // checkins
    'gratitude' |
    'mindful-reflection' |
    'self-reflection' |
    'affirmation' |
    'daily-snapshot' |
    'progress-achievements' |
    'health-overview' |
    'special-moments-connections'
;

const templateBodyById: { [key in ITemplateId]: string } = {
    // community feed
    'ride': `
We’d really appreciate some help with driving!

Event:


Date & Time:


Pick-Up Location:


Destination:


Other Notes:


If you’re available to assist, please let me know! Your support means so much.`,

    'groceries': `
We could use some help with groceries or meals if you’re available!

We’re looking for:


Allergies:


Requested days/times:


If anyone can lend a hand, it would be greatly appreciated!`,

    'rest': `
Hello! I’m looking for some extra hands soon. Let us know if you have any availability for the following…

Start Date & Time:


End Date & Time:


Location:


Other Notes:


Even partial coverage would be super helpful- thanks!`,

    'errand': `
Is anyone free to help run a quick errand?

We need assistance with:


On/By:


Please let us know if you’re available to assist!`,

    'pet-care': `
We’re looking for some help with pet care. Let us know if you can lend a hand with any of the following-it would be a huge help!

Sitting For:


Start Date & Time:


End Date & Time:


Location:


Other Notes:


`,

    'visits': `
We’d love your visit or check-in! Below are some time options that would work.

Date(s) & Time(s):


Location:


Other Notes:


If you’re free, definitely stop on by!`,

    // checkins
    'gratitude': `
🙏 Gratitude

List three things you are grateful for today:

1.\xA0`,

    'mindful-reflection': `
🌅 Mindful Reflection

Describe a moment today when you felt completely present. What were you doing, and how did it make you feel?
➡️\xA0`,

    'self-reflection': `
🤔 Self-Reflection

How am I feeling right now?
➡️\xA0

What makes me hopeful?
➡️\xA0

What makes me worried?
➡️\xA0

What can I accept that I cannot change?
➡️\xA0`,

    'affirmation': `
🙌 Affirmation

Write down three positive things about yourself or your caregiving today.
How do these positive thoughts make you feel and change your outlook?

1.\xA0`,

    'daily-snapshot': `
📸 Daily Snapshot

Record an overview of the day’s caregiving.

Caregiving Observations:
➡️\xA0

Recipient’s Mood/Behavior:
➡️\xA0

Highlights of the Day:
➡️\xA0

Concerns or Challenges:
➡️\xA0`,

    'progress-achievements': `
🏆 Progress & Achievements

Celebrate and document important milestones.

Milestone Achieved:
➡️\xA0

Additional Context:
➡️\xA0

Next Steps or Follow-Up Goals:
➡️\xA0`,

    'health-overview': `
🩺 Health Overview

Log observations specific to care recipient health.

Vital Signs/Health Observations:
➡️\xA0

Symptoms Noted:
➡️\xA0

Action Items or Adjustments:
➡️\xA0`,

    'special-moments-connections': `
✨ Special Moments & Connections

Capture meaningful moments, conversations, or interactions with the care recipient or visitors.

Memorable Interaction:
➡️\xA0

Conversation Highlights:
➡️\xA0

Visitor Details:
➡️\xA0

Impact on Recipient’s Day:
➡️\xA0`
};

const templateLabelById: { [key in ITemplateId]: string } = {
    // community feed
    'ride': '🚙 Request Ride',
    'groceries': '🥗 Grocery / Meal Help',
    'rest': '☕ Rest/Break',
    'errand': '🛒 Errand Help',
    'pet-care': '🐾 Pet Care',
    'visits': '👋 Check-In / Visits',

    // checkins
    'gratitude': '🙏 Gratitude',
    'mindful-reflection': '🌅 Mindful Reflection',
    'self-reflection': '🤔 Self-Reflection',
    'affirmation': '🙌 Affirmation',
    'daily-snapshot': '📸 Daily Snapshot',
    'progress-achievements': '🏆 Progress & Achievements',
    'health-overview': '🩺 Health Overview',
    'special-moments-connections': '✨ Special Moments & Connections'
};

const templateFocusLocationById: { [key in ITemplateId]: number | undefined } = {
    // community feed
    'ride': 55,
    'groceries': 88,
    'rest': 126,
    'errand': 69,
    'pet-care': 143,
    'visits': 99,

    // checkins
    'gratitude': undefined,
    'mindful-reflection': undefined,
    'self-reflection': 51,
    'affirmation': undefined,
    'daily-snapshot': 92,
    'progress-achievements': 97,
    'health-overview': 109,
    'special-moments-connections': 156
};

// these will be buttons
const templateButtonById: { [key in ITemplateId]?: boolean } = {
    'ride': true,
    'groceries': true
};

const templateLabelForTitleById: { [key in ITemplateId]?: boolean | undefined } = {

};

const templateIdsByTarget: { [target: string]: ITemplateId[] } = {
    'patient': [
        // community feed
        // for the ones that are buttons (templateButtonById), this is the order the buttons will be in,
        // as well as the order in the menu
        'groceries',
        'ride',
        'rest',
        'errand',
        'pet-care',
        'visits'
    ],

    'household/member': [],
    'household': [],

    // checkins
    'checkin/personal': [
        'gratitude',
        'mindful-reflection',
        'self-reflection',
        'affirmation'
    ],
    'checkin/care': [
        'daily-snapshot',
        'progress-achievements',
        'health-overview',
        'special-moments-connections'
    ]
};

export class TemplatesConfig {
    static inject = () => [CustomOrgTemplates];

    constructor(orgTemplates: CustomOrgTemplates){
        this.templates = [
            ...this.buildNormalTemplates(),
            ...orgTemplates.getTemplates()
        ];
    }

    templates: INoteTemplate[];

    private buildNormalTemplates = (): INoteTemplate[] => {
        const templates:  INoteTemplate[] = [];
        for (const targetString of Object.keys(templateIdsByTarget)){
            const [section, subsection] = targetString.split('/');

            let target: INoteTemplateTarget;

            if (section === 'checkin') {
                target = {
                    section,
                    checkinType: subsection as ICheckinType,
                };
            } else {
                target = {
                    section: section as ICareMapSection,
                    forMember: subsection === 'member'
                };
            }

            for (const templateId of templateIdsByTarget[targetString]){
                const body = templateBodyById[templateId];
                const label = templateLabelById[templateId];
                const focusLocation = templateFocusLocationById[templateId];
                const useLabelForTitle = templateLabelForTitleById[templateId];
                const isButton = templateButtonById[templateId];
                templates.push({
                    templateId,
                    label,
                    body,
                    target,
                    focusLocation,
                    useLabelForTitle,
                    isButton
                });
            }
        }

        return templates;
    };
};
